import { useRef, useCallback } from 'react'
import { Check } from 'lucide-react'
import type { MobilePreset } from '@/lib/api/controlPlaneClient'

interface PresetPillsProps {
  presets: Array<{ preset: MobilePreset; nameTh: string; nameEn: string }>
  activePreset: MobilePreset
  selectedPreset: MobilePreset | null
  effectivePreset: MobilePreset
  onSelect: (preset: MobilePreset) => void
  isLoading: boolean
}

export function PresetPills({
  presets,
  activePreset,
  selectedPreset,
  effectivePreset,
  onSelect,
  isLoading,
}: PresetPillsProps) {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])
  const focusedIndexRef = useRef(0)

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const count = presets.length
      let idx = focusedIndexRef.current

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault()
        idx = (idx + 1) % count
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault()
        idx = (idx - 1 + count) % count
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSelect(presets[idx].preset)
        return
      } else {
        return
      }

      focusedIndexRef.current = idx
      buttonRefs.current[idx]?.focus()
    },
    [presets, onSelect]
  )

  if (isLoading) {
    return (
      <div className="flex gap-2">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-[60px] flex-1 animate-pulse rounded bg-gray-100 motion-reduce:animate-none"
          />
        ))}
      </div>
    )
  }

  // Determine which pill index should be focusable (roving tabindex)
  const effectiveIndex = presets.findIndex((p) => p.preset === effectivePreset)

  return (
    <div
      className="flex gap-2"
      role="radiogroup"
      aria-label="Mobile experience presets"
      onKeyDown={handleKeyDown}
    >
      {presets.map((p, i) => {
        const isActive = p.preset === activePreset
        const isSelected = p.preset === selectedPreset && selectedPreset !== activePreset
        const isEffective = p.preset === effectivePreset

        return (
          <button
            key={p.preset}
            ref={(el) => { buttonRefs.current[i] = el }}
            type="button"
            role="radio"
            aria-checked={isEffective}
            aria-label={`${p.nameTh} (${p.nameEn})`}
            tabIndex={i === (effectiveIndex >= 0 ? effectiveIndex : 0) ? 0 : -1}
            onClick={() => onSelect(p.preset)}
            onFocus={() => { focusedIndexRef.current = i }}
            className={`relative flex h-[60px] flex-1 flex-col items-center justify-center rounded border-2 transition-colors transition-transform duration-150 motion-reduce:transition-none active:scale-[0.98] motion-reduce:active:scale-100 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
              isSelected
                ? 'border-blue-500 bg-blue-50 shadow-sm'
                : isActive
                  ? 'border-green-200 bg-green-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            {isActive && (
              <div className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-green-500 shadow-sm">
                <Check className="h-3 w-3 text-white" strokeWidth={3} />
              </div>
            )}
            <span className="text-sm font-semibold text-gray-900">{p.nameTh}</span>
            <span className="text-[11px] text-gray-500">{p.nameEn}</span>
          </button>
        )
      })}
    </div>
  )
}
